import { Directive, ElementRef, Input, OnChanges, Renderer2, SimpleChanges, inject } from '@angular/core';

/**
 * Native-Angular port of AngularJS's built-in `<ng-pluralize>` directive.
 * v1 usage:
 *   <ng-pluralize count="vm.X" when="{'one': 'campaign', 'other': 'campaigns'}"></ng-pluralize>
 *
 * Sets the host element's textContent to the pluralized form based on
 * `count`. Mirrors AngularJS pluralize semantics minimally:
 *   - count == 1 -> when.one (also 'one' / '=1')
 *   - count == 0 -> when.zero (also 'zero' / '=0') if defined, else when.other
 *   - otherwise -> when.other
 *
 * @angular/common has `i18nPlural` pipe + `[ngPlural]` for newer code,
 * but porting the templates verbatim is faster than rewriting every
 * `<ng-pluralize>` use. Drop this shim when those use-sites get rewritten
 * to `{{ count | i18nPlural: map }}` at the Angular-21 milestone.
 */
@Directive({
    selector: 'ng-pluralize',
    standalone: false
})
export class NgPluralizeDirective implements OnChanges {
  @Input() count: number | string | null = 0;
  @Input() when: string | Record<string, string> | undefined;

    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

  ngOnChanges(_: SimpleChanges): void {
    this.refresh();
  }

  private refresh(): void {
    const n = typeof this.count === 'number' ? this.count : Number(this.count ?? 0);
    const map = this.parseMap(this.when);
    let text = '';
    if (n === 0 && (map['zero'] ?? map['=0'])) text = map['zero'] ?? map['=0'];
    else if (n === 1 && (map['one'] ?? map['=1'])) text = map['one'] ?? map['=1'];
    else text = map['other'] ?? '';
    this.renderer.setProperty(this.el.nativeElement, 'textContent', text);
  }

  private parseMap(expr: string | Record<string, string> | undefined): Record<string, string> {
    if (typeof expr === 'object' && expr !== null) return expr;
    if (typeof expr !== 'string' || !expr) return {};
    try {
      return JSON.parse(expr.replace(/'/g, '"')) ?? {};
    } catch {
      return {};
    }
  }
}
